import { useState, useRef, useCallback } from 'react';

export const useVideoControls = (onPlay?: () => void, onPause?: () => void) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = useCallback(async () => {
    if (videoRef.current) {
      try {
        await videoRef.current.play();
        setIsPlaying(true);
        setIsPaused(false);
        setHasError(false);
        onPlay?.();
      } catch (error) {
        // Handle play() promise rejection (e.g., browser auto-play policy)
        console.error('Video play failed:', error);
        setHasError(true);
        setIsPlaying(false);
      }
    }
  }, [onPlay]);

  const handlePause = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
      setIsPaused(true);
      onPause?.();
    }
  }, [onPause]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    onPause?.();
  }, [onPause]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsPlaying(false);
    setIsPaused(false);
    console.error('Video loading or playback error');
  }, []);

  const pause = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
      setIsPaused(true);
      onPause?.();
    }
  }, [onPause]);

  return {
    videoRef,
    isPlaying,
    isPaused,
    hasError,
    handlePlay,
    handlePause,
    handleEnded,
    handleError,
    pause
  };
};
